import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  BulkImportResult,
  BulkImportRow,
  BulkMovePrepConfigItemsPayload,
  CreatePrepConfigItemPayload,
  CreatePrepConfigSectionPayload,
  ManagerPrepType,
  PrepConfigResponse,
  PrepConfigSection,
  UnmappedIngredient,
  PrepConfigIngredientHealth,
  PrepConfigSourceIssue,
  SeedReconciliationLog,
  UomOptionsByTier,
  UpdatePrepConfigItemPayload,
  UpdatePrepConfigSectionPayload,
  UptReviewCategory,
  UpsertUptReviewCategoryPayload,
  UpsertUptReviewItemPayload,
  UpdateUptReviewItemByIdPayload,
  ResolveUptReviewItemPayload,
  UptReviewItemMapping
} from '@/src/types/apps/prepConfig'

type HttpClient = {
  get: Function
  post: Function
  put: Function
  patch: Function
  delete: Function
}

function unwrap<T>(data: unknown): T {
  let current: unknown = data
  const outer = current as { data?: unknown }
  if (outer && typeof outer === 'object' && 'data' in outer) {
    current = outer.data
  }

  // AuditableResponse from Nest controllers: { data: T, _metadata } — already
  // wrapped once more by ResponseTransformInterceptor.
  const auditable = current as { data?: T; _metadata?: unknown }
  if (auditable && typeof auditable === 'object' && 'data' in auditable && '_metadata' in auditable) {
    current = auditable.data
  }

  return current as T
}

function sectionsKey(
  prepType: ManagerPrepType
): keyof Pick<PrepConfigResponse, 'cookPrep' | 'linePrep' | 'onHandPrep'> {
  if (prepType === 'COOK') return 'cookPrep'
  if (prepType === 'LINE') return 'linePrep'

  return 'onHandPrep'
}

function sortSections(sections: PrepConfigSection[]) {
  sections.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
}

function sortItems(section: PrepConfigSection) {
  section.items.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
}

interface PrepConfigState {
  config: PrepConfigResponse | null
  unmappedIngredients: UnmappedIngredient[]
  sourceIssues: PrepConfigSourceIssue[]
  catalogueSyncedAt: string | null
  unmappedLoading: boolean
  reconciliationLog: SeedReconciliationLog | null
  reconciliationLoading: boolean
  uomOptions: UomOptionsByTier
  uptReviewCategories: UptReviewCategory[]
  uptReviewLoading: boolean
  loading: boolean
  saving: boolean
  error: string | null
  success: string | null
}

/** Replace or insert a section view returned by create/update into local config. */
function upsertSectionInConfig(state: PrepConfigState, section: PrepConfigSection) {
  if (!state.config) return
  const keys: Array<keyof Pick<PrepConfigResponse, 'cookPrep' | 'linePrep' | 'onHandPrep'>> = [
    'cookPrep',
    'linePrep',
    'onHandPrep'
  ]
  let previousKey: (typeof keys)[number] | null = null
  let previousItems: PrepConfigSection['items'] | undefined
  for (const key of keys) {
    const list = state.config[key]
    const idx = list.findIndex(s => s.id === section.id)
    if (idx >= 0) {
      previousKey = key
      previousItems = list[idx].items
      list.splice(idx, 1)
      break
    }
  }
  const targetKey = section.prepType != null ? sectionsKey(section.prepType) : (previousKey ?? 'cookPrep')
  state.config[targetKey].push({
    ...section,
    items: section.items?.length ? section.items : (previousItems ?? section.items ?? [])
  })
  sortSections(state.config[targetKey])
}

function removeSectionFromConfig(state: PrepConfigState, sectionId: number) {
  if (!state.config) return
  for (const key of ['cookPrep', 'linePrep', 'onHandPrep'] as const) {
    const list = state.config[key]
    const idx = list.findIndex(s => s.id === sectionId)
    if (idx >= 0) list.splice(idx, 1)
  }
}

const emptyUomOptions: UomOptionsByTier = { tier1: [], tier2: [], tier3: [], tier4: [] }

const initialState: PrepConfigState = {
  config: null,
  unmappedIngredients: [],
  sourceIssues: [],
  catalogueSyncedAt: null,
  unmappedLoading: false,
  reconciliationLog: null,
  reconciliationLoading: false,
  uomOptions: emptyUomOptions,
  uptReviewCategories: [],
  uptReviewLoading: false,
  loading: false,
  saving: false,
  error: null,
  success: null
}

export const fetchPrepConfig = createAsyncThunk<
  PrepConfigResponse,
  void,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/fetchPrepConfig', async (_, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.get('/preplists/config')

    return unwrap<PrepConfigResponse>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to load prep config')
  }
})

/**
 * Reuses GET /cerebro-product-companies/list (productName filter searches
 * both product name and Product Name Number). Used by Prep Config mapping UX
 * — does not write into adminToolsIngredients slice state.
 */
export const searchCerebroIngredients = createAsyncThunk<
  Array<{ productNameNumber: string; productName: string }>,
  string,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/searchCerebroIngredients', async (term, { rejectWithValue, extra }) => {
  const trimmed = term.trim()
  if (!trimmed) return []

  try {
    const { data } = await extra.httpClient.get('cerebro-product-companies/list', {
      params: {
        take: 25,
        skip: 0,
        sortBy: 'productName',
        filters: { productName: trimmed }
      }
    })

    const list = unwrap<Array<{ productNameNumber?: string; productName?: string }> | { data?: unknown }>(data)
    const rows = Array.isArray(list)
      ? list
      : Array.isArray((list as { data?: unknown })?.data)
        ? (list as { data: Array<{ productNameNumber?: string; productName?: string }> }).data
        : []

    return rows
      .filter(row => Boolean(row?.productNameNumber?.trim()))
      .map(row => ({
        productNameNumber: row.productNameNumber!.trim(),
        productName: row.productName?.trim() || ''
      }))
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to search Cerebro ingredients')
  }
})

export const fetchUnmappedIngredients = createAsyncThunk<
  PrepConfigIngredientHealth,
  void,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/fetchUnmappedIngredients', async (_, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.get('/preplists/config/unmapped-ingredients')
    const health = unwrap<PrepConfigIngredientHealth | UnmappedIngredient[]>(data)

    // Back-compat if an older BE still returns a bare array.
    if (Array.isArray(health)) {
      return { catalogueSyncedAt: null, unmapped: health, sourceIssues: [] }
    }

    return {
      catalogueSyncedAt: health.catalogueSyncedAt ?? null,
      unmapped: Array.isArray(health.unmapped) ? health.unmapped : [],
      sourceIssues: Array.isArray(health.sourceIssues) ? health.sourceIssues : []
    }
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to load unmapped ingredients')
  }
})

export const fetchSeedReconciliationLog = createAsyncThunk<
  SeedReconciliationLog,
  void,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/fetchSeedReconciliationLog', async (_, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.get('/preplists/config/seed-reconciliation-log')

    return unwrap<SeedReconciliationLog>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to load reconciliation log')
  }
})

export const fetchUomOptions = createAsyncThunk<
  UomOptionsByTier,
  void,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/fetchUomOptions', async (_, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.get('/preplists/config/uom-options')
    const options = unwrap<Partial<UomOptionsByTier>>(data)

    return {
      tier1: Array.isArray(options?.tier1) ? options.tier1 : [],
      tier2: Array.isArray(options?.tier2) ? options.tier2 : [],
      tier3: Array.isArray(options?.tier3) ? options.tier3 : [],
      tier4: Array.isArray(options?.tier4) ? options.tier4 : []
    }
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to load UOM options')
  }
})

// ─── (2 Sep 2026) Tab 2 UPT Review V3 configuration ────────────────────────

export const fetchUptReviewCategories = createAsyncThunk<
  UptReviewCategory[],
  void,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/fetchUptReviewCategories', async (_, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.get('/preplists/config/upt-review-categories')

    return unwrap<UptReviewCategory[]>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to load UPT Review categories')
  }
})

export const createUptReviewCategory = createAsyncThunk<
  UptReviewCategory,
  UpsertUptReviewCategoryPayload,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/createUptReviewCategory', async (payload, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.post('/preplists/config/upt-review-categories', payload)

    return unwrap<UptReviewCategory>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create UPT Review category')
  }
})

export const updateUptReviewCategory = createAsyncThunk<
  UptReviewCategory,
  { categoryId: number; payload: UpsertUptReviewCategoryPayload },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/updateUptReviewCategory', async ({ categoryId, payload }, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.put(`/preplists/config/upt-review-categories/${categoryId}`, payload)

    return unwrap<UptReviewCategory>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update UPT Review category')
  }
})

export const deleteUptReviewCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/deleteUptReviewCategory', async (categoryId, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.delete(`/preplists/config/upt-review-categories/${categoryId}`)

    return categoryId
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete UPT Review category')
  }
})

export const upsertUptReviewItemMapping = createAsyncThunk<
  void,
  { prepConfigItemId: number; payload: UpsertUptReviewItemPayload },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/upsertUptReviewItemMapping', async ({ prepConfigItemId, payload }, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.put(`/preplists/config/upt-review-items/${prepConfigItemId}`, payload)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update UPT Review membership')
  }
})

export const removeUptReviewItemMapping = createAsyncThunk<
  void,
  number,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/removeUptReviewItemMapping', async (prepConfigItemId, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.delete(`/preplists/config/upt-review-items/${prepConfigItemId}`)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to remove from UPT Review')
  }
})

export const updateUptReviewItemById = createAsyncThunk<
  UptReviewItemMapping,
  { reviewItemId: number; payload: UpdateUptReviewItemByIdPayload },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/updateUptReviewItemById', async ({ reviewItemId, payload }, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.put(`/preplists/config/upt-review-items/by-id/${reviewItemId}`, payload)

    return unwrap<UptReviewItemMapping>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update UPT Review item')
  }
})

export const resolveUptReviewItem = createAsyncThunk<
  UptReviewItemMapping,
  { reviewItemId: number; payload: ResolveUptReviewItemPayload },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/resolveUptReviewItem', async ({ reviewItemId, payload }, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.patch(`/preplists/config/upt-review-items/${reviewItemId}/resolve`, payload)

    return unwrap<UptReviewItemMapping>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to resolve pending UPT item')
  }
})

export const createPrepConfigSection = createAsyncThunk<
  PrepConfigSection,
  CreatePrepConfigSectionPayload,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/createPrepConfigSection', async (payload, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.post('/preplists/config/sections', payload)

    return unwrap<PrepConfigSection>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create section')
  }
})

export const updatePrepConfigSection = createAsyncThunk<
  PrepConfigSection,
  { sectionId: number; payload: UpdatePrepConfigSectionPayload },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/updatePrepConfigSection', async ({ sectionId, payload }, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.put(`/preplists/config/sections/${sectionId}`, payload)

    return unwrap<PrepConfigSection>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update section')
  }
})

export const deletePrepConfigSection = createAsyncThunk<
  number,
  number,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/deletePrepConfigSection', async (sectionId, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.delete(`/preplists/config/sections/${sectionId}`)

    return sectionId
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete section')
  }
})

export const createPrepConfigItem = createAsyncThunk<
  void,
  { sectionId: number; payload: CreatePrepConfigItemPayload },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/createPrepConfigItem', async ({ sectionId, payload }, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.post(`/preplists/config/sections/${sectionId}/items`, payload)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create item')
  }
})

export const updatePrepConfigItem = createAsyncThunk<
  void,
  { itemId: number; payload: UpdatePrepConfigItemPayload },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/updatePrepConfigItem', async ({ itemId, payload }, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.put(`/preplists/config/items/${itemId}`, payload)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update item')
  }
})

export const deletePrepConfigItem = createAsyncThunk<
  number,
  number,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/deletePrepConfigItem', async (itemId, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.delete(`/preplists/config/items/${itemId}`)

    return itemId
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete item')
  }
})

export const bulkImportPrepConfig = createAsyncThunk<
  BulkImportResult,
  BulkImportRow[],
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/bulkImportPrepConfig', async (rows, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.post('/preplists/config/bulk-import', { rows })

    return unwrap<BulkImportResult>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to bulk import')
  }
})

export const bulkMovePrepConfigItems = createAsyncThunk<
  { moved: number },
  BulkMovePrepConfigItemsPayload,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/bulkMovePrepConfigItems', async (payload, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.patch('/preplists/config/items/bulk-move', payload)

    return unwrap<{ moved: number }>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to move items')
  }
})

export const reorderPrepConfigSections = createAsyncThunk<
  void,
  { prepType: ManagerPrepType; sectionAId: number; sectionBId: number; sortOrderA: number; sortOrderB: number },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/reorderPrepConfigSections', async (payload, { rejectWithValue, extra }) => {
  try {
    await Promise.all([
      extra.httpClient.put(`/preplists/config/sections/${payload.sectionAId}`, {
        sortOrder: payload.sortOrderB
      }),
      extra.httpClient.put(`/preplists/config/sections/${payload.sectionBId}`, {
        sortOrder: payload.sortOrderA
      })
    ])
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to reorder sections')
  }
})

export const reorderPrepConfigItems = createAsyncThunk<
  void,
  { sectionId: number; itemAId: number; itemBId: number; sortOrderA: number; sortOrderB: number },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/reorderPrepConfigItems', async (payload, { rejectWithValue, extra }) => {
  try {
    await Promise.all([
      extra.httpClient.put(`/preplists/config/items/${payload.itemAId}`, {
        sortOrder: payload.sortOrderB
      }),
      extra.httpClient.put(`/preplists/config/items/${payload.itemBId}`, {
        sortOrder: payload.sortOrderA
      })
    ])
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to reorder items')
  }
})

const prepConfigSlice = createSlice({
  name: 'prepConfig',
  initialState,
  reducers: {
    resetPrepConfigMessages(state) {
      state.error = null
      state.success = null
    },
    swapSectionSortOrders(state, action: PayloadAction<{ prepType: ManagerPrepType; indexA: number; indexB: number }>) {
      if (!state.config) return
      const key = sectionsKey(action.payload.prepType)
      const sections = state.config[key]
      const { indexA, indexB } = action.payload
      const a = sections[indexA]
      const b = sections[indexB]
      if (!a || !b) return
      const aOrder = a.sortOrder
      a.sortOrder = b.sortOrder
      b.sortOrder = aOrder
      sections[indexA] = b
      sections[indexB] = a
      sortSections(sections)
    },
    swapItemSortOrders(state, action: PayloadAction<{ sectionId: number; indexA: number; indexB: number }>) {
      if (!state.config) return
      const all = [...state.config.cookPrep, ...state.config.linePrep, ...state.config.onHandPrep]
      const section = all.find(s => s.id === action.payload.sectionId)
      if (!section) return
      const { indexA, indexB } = action.payload
      const a = section.items[indexA]
      const b = section.items[indexB]
      if (!a || !b) return
      const aOrder = a.sortOrder
      a.sortOrder = b.sortOrder
      b.sortOrder = aOrder
      section.items[indexA] = b
      section.items[indexB] = a
      sortItems(section)
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPrepConfig.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPrepConfig.fulfilled, (state, action) => {
        state.loading = false
        state.config = action.payload
      })
      .addCase(fetchPrepConfig.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to load prep config'
      })

    builder
      .addCase(fetchUnmappedIngredients.pending, state => {
        state.unmappedLoading = true
      })
      .addCase(fetchUnmappedIngredients.fulfilled, (state, action) => {
        state.unmappedLoading = false
        state.unmappedIngredients = action.payload.unmapped
        state.sourceIssues = action.payload.sourceIssues
        state.catalogueSyncedAt = action.payload.catalogueSyncedAt
      })
      .addCase(fetchUnmappedIngredients.rejected, (state, action) => {
        state.unmappedLoading = false
        state.error = action.payload ?? 'Failed to load unmapped ingredients'
      })

    builder
      .addCase(fetchSeedReconciliationLog.pending, state => {
        state.reconciliationLoading = true
      })
      .addCase(fetchSeedReconciliationLog.fulfilled, (state, action) => {
        state.reconciliationLoading = false
        state.reconciliationLog = action.payload
      })
      .addCase(fetchSeedReconciliationLog.rejected, (state, action) => {
        state.reconciliationLoading = false
        state.error = action.payload ?? 'Failed to load reconciliation log'
      })

    builder.addCase(fetchUomOptions.fulfilled, (state, action) => {
      state.uomOptions = action.payload
    })

    builder
      .addCase(fetchUptReviewCategories.pending, state => {
        state.uptReviewLoading = true
      })
      .addCase(fetchUptReviewCategories.fulfilled, (state, action) => {
        state.uptReviewLoading = false
        state.uptReviewCategories = action.payload
      })
      .addCase(fetchUptReviewCategories.rejected, (state, action) => {
        state.uptReviewLoading = false
        state.error = action.payload ?? 'Failed to load UPT Review categories'
      })

    const saving = (state: PrepConfigState) => {
      state.saving = true
      state.error = null
    }
    const saved = (state: PrepConfigState, message: string) => {
      state.saving = false
      state.success = message
    }
    const saveFailed = (state: PrepConfigState, action: { payload?: string }) => {
      state.saving = false
      state.error = action.payload ?? 'Save failed'
    }
    const silentDone = (state: PrepConfigState) => {
      state.saving = false
    }

    builder
      .addCase(createPrepConfigSection.pending, saving)
      .addCase(createPrepConfigSection.fulfilled, (state, action) => {
        upsertSectionInConfig(state, action.payload)
        saved(state, `Section "${action.payload.name}" created`)
      })
      .addCase(createPrepConfigSection.rejected, saveFailed)

    builder
      .addCase(updatePrepConfigSection.pending, saving)
      .addCase(updatePrepConfigSection.fulfilled, (state, action) => {
        upsertSectionInConfig(state, action.payload)
        saved(state, `Section "${action.payload.name}" updated`)
      })
      .addCase(updatePrepConfigSection.rejected, saveFailed)

    builder
      .addCase(deletePrepConfigSection.pending, saving)
      .addCase(deletePrepConfigSection.fulfilled, (state, action) => {
        removeSectionFromConfig(state, action.payload)
        saved(state, 'Section deleted')
      })
      .addCase(deletePrepConfigSection.rejected, saveFailed)

    builder
      .addCase(createPrepConfigItem.pending, saving)
      .addCase(createPrepConfigItem.fulfilled, state => {
        saved(state, 'Item mapped')
      })
      .addCase(createPrepConfigItem.rejected, saveFailed)

    builder
      .addCase(updatePrepConfigItem.pending, saving)
      .addCase(updatePrepConfigItem.fulfilled, state => {
        saved(state, 'Item updated')
      })
      .addCase(updatePrepConfigItem.rejected, saveFailed)

    builder
      .addCase(deletePrepConfigItem.pending, saving)
      .addCase(deletePrepConfigItem.fulfilled, state => {
        saved(state, 'Item deleted')
      })
      .addCase(deletePrepConfigItem.rejected, saveFailed)

    builder
      .addCase(createUptReviewCategory.pending, saving)
      .addCase(createUptReviewCategory.fulfilled, (state, action) => {
        state.uptReviewCategories = [...state.uptReviewCategories, action.payload].sort(
          (a, b) => a.sortOrder - b.sortOrder
        )
        saved(state, `UPT Review category "${action.payload.name}" created`)
      })
      .addCase(createUptReviewCategory.rejected, saveFailed)

    builder
      .addCase(updateUptReviewCategory.pending, saving)
      .addCase(updateUptReviewCategory.fulfilled, (state, action) => {
        state.uptReviewCategories = state.uptReviewCategories
          .map(c => (c.id === action.payload.id ? action.payload : c))
          .sort((a, b) => a.sortOrder - b.sortOrder)
        saved(state, `UPT Review category "${action.payload.name}" updated`)
      })
      .addCase(updateUptReviewCategory.rejected, saveFailed)

    builder
      .addCase(deleteUptReviewCategory.pending, saving)
      .addCase(deleteUptReviewCategory.fulfilled, (state, action) => {
        state.uptReviewCategories = state.uptReviewCategories.filter(c => c.id !== action.payload)
        saved(state, 'UPT Review category deleted')
      })
      .addCase(deleteUptReviewCategory.rejected, saveFailed)

    builder
      .addCase(upsertUptReviewItemMapping.pending, saving)
      .addCase(upsertUptReviewItemMapping.fulfilled, state => {
        saved(state, 'UPT Review membership updated')
      })
      .addCase(upsertUptReviewItemMapping.rejected, saveFailed)

    builder
      .addCase(removeUptReviewItemMapping.pending, saving)
      .addCase(removeUptReviewItemMapping.fulfilled, state => {
        saved(state, 'Removed from UPT Review')
      })
      .addCase(removeUptReviewItemMapping.rejected, saveFailed)

    builder
      .addCase(updateUptReviewItemById.pending, saving)
      .addCase(updateUptReviewItemById.fulfilled, state => {
        saved(state, 'UPT Review item updated')
      })
      .addCase(updateUptReviewItemById.rejected, saveFailed)

    builder
      .addCase(resolveUptReviewItem.pending, saving)
      .addCase(resolveUptReviewItem.fulfilled, state => {
        saved(state, 'Pending UPT item resolved')
      })
      .addCase(resolveUptReviewItem.rejected, saveFailed)

    builder
      .addCase(bulkImportPrepConfig.pending, saving)
      .addCase(bulkImportPrepConfig.fulfilled, (state, action) => {
        const { sectionsCreated, itemsCreated, itemsUpdated } = action.payload
        saved(
          state,
          `Bulk import complete: ${sectionsCreated} section(s) created, ${itemsCreated} item(s) created, ${itemsUpdated} item(s) updated`
        )
      })
      .addCase(bulkImportPrepConfig.rejected, saveFailed)

    builder
      .addCase(bulkMovePrepConfigItems.pending, saving)
      .addCase(bulkMovePrepConfigItems.fulfilled, (state, action) => {
        saved(state, `Moved ${action.payload.moved} item(s)`)
      })
      .addCase(bulkMovePrepConfigItems.rejected, saveFailed)

    builder
      .addCase(reorderPrepConfigSections.pending, saving)
      .addCase(reorderPrepConfigSections.fulfilled, silentDone)
      .addCase(reorderPrepConfigSections.rejected, saveFailed)

    builder
      .addCase(reorderPrepConfigItems.pending, saving)
      .addCase(reorderPrepConfigItems.fulfilled, silentDone)
      .addCase(reorderPrepConfigItems.rejected, saveFailed)
  }
})

export const { resetPrepConfigMessages, swapSectionSortOrders, swapItemSortOrders } = prepConfigSlice.actions
export default prepConfigSlice.reducer
